import {
  BasicInfoForTssCampaign,
  extCampaignKind,
  ExtCampaignKind,
  N_ArtworkSpec,
  N_Placement,
  N_Specifications,
} from "@specsheet-common/shared-types";
import { getDurations } from "./specifications/duration";

export const ATTR_SLOT_LENGTH = "Slot length";
export const ATTR_SPOT_DURATION = "Spot Duration";

export type StandardColumnPlacementOptions = {
  externalCampaignInfo?: BasicInfoForTssCampaign | null;
};

/** QMS, OOH, JCD(+NZ): show Slot Length column; other imports / manual bookings → blank. */
const SLOT_LENGTH_IMPORT_KINDS = new Set<string>([
  extCampaignKind.QMS,
  extCampaignKind.OOH,
  extCampaignKind.JCD,
  extCampaignKind.JCDNZ,
]);

export const parseSlotLengthImportKind = (
  externalCampaignInfo: BasicInfoForTssCampaign | null | undefined
): ExtCampaignKind | null => {
  if (!externalCampaignInfo?.extCampaignRef) {
    return null;
  }
  const prefix = externalCampaignInfo.extCampaignRef.split("-")[0];
  return SLOT_LENGTH_IMPORT_KINDS.has(prefix)
    ? (prefix as ExtCampaignKind)
    : null;
};

const getAttributeValue = (
  attrs: Array<{ name: string; value: string }> | undefined,
  name: string
): string | null => attrs?.find((a) => a.name === name)?.value ?? null;

const getSpecAttributeValue = (
  spec: N_ArtworkSpec,
  name: string
): string | null => {
  if (spec.kind !== "digitalStatic" && spec.kind !== "video") {
    return null;
  }
  return spec.value.attributes?.find((a) => a.name === name)?.value ?? null;
};

const walkSpecForAttributeName = (
  specifications: N_Specifications | undefined,
  name: string
): string | null => {
  if (!specifications?.value?.length) {
    return null;
  }
  for (const named of specifications.value) {
    const v = getSpecAttributeValue(named.value, name);
    if (v) {
      return v;
    }
  }
  return null;
};

/** QMS: "Slot length" only (placement + spec). */
const getQmsSlotLength = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  }
): string | null =>
  getAttributeValue(placement.attributes, ATTR_SLOT_LENGTH) ??
  walkSpecForAttributeName(placement.specifications, ATTR_SLOT_LENGTH);

/** OOH: "Spot Duration", then "Slot length" on placement + spec. */
const getOohSlotLength = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  }
): string | null =>
  getAttributeValue(placement.attributes, ATTR_SPOT_DURATION) ??
  getAttributeValue(placement.attributes, ATTR_SLOT_LENGTH) ??
  walkSpecForAttributeName(placement.specifications, ATTR_SPOT_DURATION) ??
  walkSpecForAttributeName(placement.specifications, ATTR_SLOT_LENGTH);

/**
 * JCD (+NZ): structured video/animated duration only (`duration (s)` → `video.durations`).
 */
export const getSlotLengthFromStructuredDurations = (
  specifications: N_Specifications | undefined
): string | null => {
  if (!specifications?.value?.length) {
    return null;
  }
  const durations = getDurations(specifications.value);
  const first = durations[0]?.value?.trim();
  return first || null;
};

/**
 * Column stays "Slot Length"; values are import-specific.
 * Requires {@link BasicInfoForTssCampaign} from the booking; otherwise blank.
 */
export const getSlotLengthDisplayValue = (
  placement: Omit<N_Placement, "attributes"> & {
    attributes?: Array<{ name: string; value: string }>;
  },
  externalCampaignInfo: BasicInfoForTssCampaign | null | undefined
): string | null => {
  const kind = parseSlotLengthImportKind(externalCampaignInfo ?? null);
  if (!kind) {
    return null;
  }

  if (kind === extCampaignKind.QMS) {
    return getQmsSlotLength(placement);
  }

  if (kind === extCampaignKind.OOH) {
    return getOohSlotLength(placement);
  }

  if (kind === extCampaignKind.JCD || kind === extCampaignKind.JCDNZ) {
    return getSlotLengthFromStructuredDurations(placement.specifications);
  }

  return null;
};
